import { useState, useEffect } from "react";
import {
    Box, Button, Card, CardContent, CircularProgress,
    TextField, Typography, Alert, InputAdornment, IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { authService } from "../services/authService";

const SetPassword = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [tokenValid, setTokenValid] = useState<boolean | null>(null);
    const [password, setPassword] = useState("");
    const [passwordAgain, setPasswordAgain] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordAgain, setShowPasswordAgain] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate("/login", { replace: true });
            return;
        }
        authService.checkTokenValid(token)
            .then((valid) => {
                if (!valid) {
                    navigate("/login", { replace: true });
                } else {
                    setTokenValid(true);
                }
            })
            .catch(() => navigate("/login", { replace: true }));
    }, [token, navigate]);

    const passwordError = password.length > 0 && password.length < 8
        ? "Şifre en az 8 karakter olmalıdır."
        : null;

    const passwordAgainError = passwordAgain.length > 0 && password !== passwordAgain
        ? "Şifreler eşleşmiyor."
        : null;

    const isValid = password.length >= 8 && password === passwordAgain;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid || !token) return;
        setLoading(true);
        setError(null);
        try {
            await authService.setPassword(token, password);
            setSuccess(true);
            setTimeout(() => navigate("/login", { replace: true }), 2000);
        } catch {
            setError("Bir hata oluştu. Lütfen tekrar deneyin.");
        } finally {
            setLoading(false);
        }
    };

    if (tokenValid === null) {
        return (
            <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f2f5" }}>
            <Card sx={{ width: 400, borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                        <Box component="img" src="/sensify-iot.png" alt="Sensify IoT" sx={{ height: 48 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={600} textAlign="center" mb={1} color="#1a1a1a">
                        Şifre Oluştur
                    </Typography>
                    <Typography variant="body2" textAlign="center" mb={3} color="text.secondary">
                        Hesabınız için şifrenizi belirleyin.
                    </Typography>

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Şifreniz başarıyla oluşturuldu. Giriş sayfasına yönlendiriliyorsunuz...
                        </Alert>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                            label="Şifre"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            size="small"
                            error={!!passwordError}
                            helperText={passwordError}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            label="Şifre Tekrar"
                            type={showPasswordAgain ? "text" : "password"}
                            value={passwordAgain}
                            onChange={(e) => setPasswordAgain(e.target.value)}
                            fullWidth
                            size="small"
                            error={!!passwordAgainError}
                            helperText={passwordAgainError}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPasswordAgain((p) => !p)} edge="end" size="small">
                                            {showPasswordAgain ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading || !isValid || success}
                            sx={{ mt: 1, py: 1.2, textTransform: "none", fontWeight: 600, backgroundColor: "var(--color-primary, #1976d2)" }}
                        >
                            {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Şifremi Oluştur"}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SetPassword;
